import { deleteData, getData, patchData, postData } from "../api/api";

export type WarehouseQueryOrigin = "CHAT" | "WIDGET" | "INSIGHT" | "ANALYTICS" | "PREP" | "ADMIN" | "EXPORT" | "OTHER";
export type WarehouseAuditStatus = "SUCCEEDED" | "FAILED" | "CANCELLED" | "REJECTED";

/** Backend `WarehouseQueryAudit`. */
export interface WarehouseQueryAudit {
  id: string;
  adaptorId: string;
  adaptorName?: string;
  engine?: string;
  origin: WarehouseQueryOrigin;
  username?: string;
  sql?: string;
  status: WarehouseAuditStatus;
  errorCode?: string;
  error?: string;
  rowCount?: number;
  scannedBytes?: number;
  estimatedBytes?: number;
  estimatedRows?: number;
  durationMs?: number;
  cacheHit?: boolean;
  engineQueryId?: string;
  executedAt: string;
}

export interface UsageGroupRow {
  key: string | null;
  label?: string | null;
  queries: number;
  scannedBytes: number;
  cacheHits: number;
  failed: number;
  rejected: number;
  durationMs: number;
}

export interface UsageSummary {
  days: number;
  since: string;
  byAdaptor: UsageGroupRow[];
  byUser: UsageGroupRow[];
  byOrigin: UsageGroupRow[];
  byDay: { day: string; queries: number; scannedBytes: number }[];
  topQueries: WarehouseQueryAudit[];
}

export interface AdaptorUsageToday {
  scannedBytes: number;
  queries: number;
  dailyScanBytesLimit?: number | null;
  dailyQueryLimit?: number | null;
}

export interface WarehouseExplainResult {
  sql: string;
  estimatedRows?: number | null;
  estimatedBytes?: number | null;
  plan?: string;
  warnings: string[];
  wouldRun: boolean;
  usageToday?: { scannedBytes: number; queries: number };
}

export interface WarehouseVerifiedQuery {
  id: string;
  adaptorId: string;
  question: string;
  sql: string;
  widgetId?: string | null;
  createdByUsername?: string | null;
  createdDate?: string;
}

export interface HistoryFilter {
  adaptorId?: string;
  origin?: WarehouseQueryOrigin;
  status?: WarehouseAuditStatus;
  username?: string;
  page?: number;
  size?: number;
}

const BASE = "/admin/warehouse";

function query(params: Record<string, string | number | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join("&")}` : "";
}

export const warehouseAdminService = {
  history: (filter: HistoryFilter = {}) =>
    getData(`${BASE}/queries${query({ ...filter })}`) as Promise<WarehouseQueryAudit[]>,
  summary: (days = 30) => getData(`${BASE}/usage/summary${query({ days })}`) as Promise<UsageSummary>,
  usageToday: (adaptorId: string) => getData(`${BASE}/adaptors/${adaptorId}/usage`) as Promise<AdaptorUsageToday>,
  explain: (adaptorId: string, sql: string) =>
    postData(`${BASE}/adaptors/${adaptorId}/explain`, { sql }) as Promise<WarehouseExplainResult>,
  verifiedQueries: (adaptorId?: string) =>
    getData(`${BASE}/verified-queries${query({ adaptorId })}`) as Promise<WarehouseVerifiedQuery[]>,
  createVerified: (adaptorId: string, question: string, sql: string) =>
    postData(`${BASE}/verified-queries`, { adaptorId, question, sql }) as Promise<WarehouseVerifiedQuery>,
  updateVerified: (id: string, question: string) =>
    patchData(`${BASE}/verified-queries/${id}`, { question }) as Promise<WarehouseVerifiedQuery>,
  deleteVerified: (id: string) => deleteData(`${BASE}/verified-queries/${id}`),
};

/** "1.5 GB" style, binary units. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return unit === 0 ? `${Math.round(value)} ${units[unit]}` : `${value.toFixed(1)} ${units[unit]}`;
}
